import {
  College,
  ColorToken,
  formatCurrency,
  PercentageBarChart,
  ROILineChart,
  roiHeadline,
} from '@backrs_ui/common-ui';
import type { ModalData } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LineIconItem } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/LineIconItem';
import {
  StatRowStyle,
  statRowMainSx,
  statRowSx,
  statRowStatsSx,
} from '@backrs_ui/grow-ui/src/components/ResourceWrapper/statColumnLayout';

import { Box } from '@mui/material';

import { useAgentContext } from './ReturnOnInvestment.agentContext';

const RETURN_ON_INVESTMENT_STRINGS = appStrings.modules.college.returnOnInvestment;

const ROI_NATIONAL_MIDPOINT_4YR = 0.58;
const ROI_NATIONAL_MIDPOINT_2YR = 0.34;

const publishedRoi = (college: College) =>
  college.scorecardRoi && roiHeadline(college.scorecardRoi) != null ? college.scorecardRoi : null;

type ReturnOnInvestmentProps = Readonly<{
  college: College;
}>;

export const ReturnOnInvestment = ({ college }: ReturnOnInvestmentProps) => {
  const isTwoYearSchool =
    college.predominant_degree_code === 'associates' ||
    college.predominant_degree_code === 'certificate';
  const nationalMidpoint = isTwoYearSchool ? ROI_NATIONAL_MIDPOINT_2YR : ROI_NATIONAL_MIDPOINT_4YR;
  const schoolType = isTwoYearSchool ? '2-year' : '4-year';
  const yearsToGraduate = isTwoYearSchool ? 4 : 8;

  const roiData = publishedRoi(college);

  useAgentContext(college, roiData, nationalMidpoint, yearsToGraduate);

  const hasMetrics =
    college.chettyData?.kq5_cond_parq1 != null || college.median_earnings_10yr != null;

  return (
    <>
      {/* GRADUATION RATE + METRIC LINE ITEMS */}
      {(college.graduation_rate_8yr != null || hasMetrics) && (
        <Box sx={[statRowSx, topRowSx]}>
          {college.graduation_rate_8yr && (
            <Box sx={statRowMainSx}>
              <PercentageBarChart
                value={college.graduation_rate_8yr}
                title={RETURN_ON_INVESTMENT_STRINGS.cards.graduationRate.title}
                tooltipText={RETURN_ON_INVESTMENT_STRINGS.cards.graduationRate.tooltip(
                  yearsToGraduate,
                )}
                topMarker={
                  college.graduation_rate_pell_8yr
                    ? {
                        label:
                          RETURN_ON_INVESTMENT_STRINGS.cards.graduationRate.pellRecipientsLabel,
                        value: college.graduation_rate_pell_8yr,
                      }
                    : undefined
                }
                bottomMarker={{
                  label:
                    RETURN_ON_INVESTMENT_STRINGS.cards.graduationRate.nationalMidpointLabel(
                      schoolType,
                    ),
                  value: nationalMidpoint,
                }}
              />
            </Box>
          )}

          {hasMetrics && (
            <Box sx={statRowStatsSx}>
              {college.chettyData?.kq5_cond_parq1 && college.chettyData?.success_rate_level && (
                <LineIconItem
                  title={RETURN_ON_INVESTMENT_STRINGS.cards.successRate.title}
                  info={{
                    kind: 'tooltip',
                    text: RETURN_ON_INVESTMENT_STRINGS.cards.successRate.tooltip,
                  }}
                  text={`${Number(college.chettyData.kq5_cond_parq1).toFixed(1)}%`}
                  chip={{
                    label:
                      RETURN_ON_INVESTMENT_STRINGS.successRateLevels[
                        college.chettyData.success_rate_level
                      ].chipLabel,
                    color: RETURN_ON_INVESTMENT_STRINGS.successRateLevels[
                      college.chettyData.success_rate_level
                    ].chipColor as ColorToken,
                    modalData: RETURN_ON_INVESTMENT_STRINGS.successRateLevels.modal as ModalData,
                  }}
                />
              )}

              {college.median_earnings_10yr && (
                <LineIconItem
                  title={RETURN_ON_INVESTMENT_STRINGS.cards.medianEarnings.title}
                  info={{
                    kind: 'tooltip',
                    text: RETURN_ON_INVESTMENT_STRINGS.cards.medianEarnings.tooltip,
                  }}
                  text={formatCurrency(college.median_earnings_10yr)}
                  chip={
                    college.median_earnings_level
                      ? {
                          label:
                            RETURN_ON_INVESTMENT_STRINGS.medianEarningsLevels[
                              college.median_earnings_level
                            ].chipLabel,
                          color: RETURN_ON_INVESTMENT_STRINGS.medianEarningsLevels[
                            college.median_earnings_level
                          ].chipColor as ColorToken,
                          modalData: RETURN_ON_INVESTMENT_STRINGS.medianEarningsLevels
                            .modal as ModalData,
                        }
                      : undefined
                  }
                />
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ROI HORIZON SUBSECTION + CHART */}
      {roiData && (
        <ROILineChart roiData={roiData} title={RETURN_ON_INVESTMENT_STRINGS.roiHorizon.title} />
      )}
    </>
  );
};

const topRowSx: StatRowStyle = ({ backrs }) => ({
  mb: backrs.spacing['spacing-2xl'],
});
